import { useState, useMemo, useCallback, useEffect, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Button,
  Checkbox,
  CircularProgress,
  Alert,
  Divider,
  Collapse,
  IconButton,
  TextField,
  InputAdornment,
} from '@mui/material';
import { ExpandMore, Search as SearchIcon, Clear as ClearIcon } from '@mui/icons-material';
import { useRolePermissions, useUpdateRolePermissions } from '@/hooks/identity/useIdentity';
import { PermissionGroup, Permission, PermissionGrant } from '@/types/identity/Identity';
import { useDebounce } from '@/hooks/useDebounce';

interface PermissionTreeProps {
  /** Role name to load/save permissions for */
  roleName: string;
}

export interface PermissionTreeHandle {
  save: () => void;
  hasChanges: boolean;
}

export const PermissionTree = forwardRef<PermissionTreeHandle, PermissionTreeProps>(
  ({ roleName }, ref) => {
    const { data, isLoading, error } = useRolePermissions(roleName);
    const updateMutation = useUpdateRolePermissions(roleName);
    const [saveStatus, setSaveStatus] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

    // Local grant state: permission name → isGranted
    const [grants, setGrants] = useState<Map<string, boolean>>(new Map());
    const [initialized, setInitialized] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [filterText, setFilterText] = useState('');
    const debouncedFilter = useDebounce(filterText, 200);

    // Initialize from server data
    useEffect(() => {
      if (data?.groups) {
        const initial = new Map<string, boolean>();
        for (const group of data.groups) {
          for (const perm of group.permissions) {
            initial.set(perm.name, perm.isGranted);
          }
        }
        setGrants(initial);
        setInitialized(true);
      }
    }, [data]);

    // Build parent→children map for cascade logic
    const childrenMap = useMemo(() => {
      const map = new Map<string, string[]>();
      if (!data?.groups) return map;
      for (const group of data.groups) {
        for (const perm of group.permissions) {
          if (perm.parentName) {
            const siblings = map.get(perm.parentName) || [];
            siblings.push(perm.name);
            map.set(perm.parentName, siblings);
          }
        }
      }
      return map;
    }, [data]);

    // Compute which permissions match the filter (null = no filter active)
    const filterResult = useMemo(() => {
      const query = debouncedFilter.trim().toLowerCase();
      if (!query || !data?.groups) return null;

      const matchingPerms = new Set<string>();   // permissions whose displayName matches
      const visiblePerms = new Set<string>();     // matchingPerms + ancestors
      const visibleGroups = new Set<string>();

      for (const group of data.groups) {
        const groupMatches = group.displayName.toLowerCase().includes(query);
        if (groupMatches) {
          // Entire group matches — show all its permissions
          visibleGroups.add(group.name);
          for (const p of group.permissions) {
            matchingPerms.add(p.name);
            visiblePerms.add(p.name);
          }
          continue;
        }

        // Check individual permissions
        for (const perm of group.permissions) {
          if (perm.displayName.toLowerCase().includes(query)) {
            matchingPerms.add(perm.name);
            visiblePerms.add(perm.name);
            // Walk up to include ancestors
            let current = perm;
            while (current.parentName) {
              visiblePerms.add(current.parentName);
              const parent = group.permissions.find((p) => p.name === current.parentName);
              if (!parent) break;
              current = parent;
            }
            visibleGroups.add(group.name);
          }
        }
      }

      return { matchingPerms, visiblePerms, visibleGroups };
    }, [debouncedFilter, data]);

    const hasChanges = useMemo(() => {
      if (!data?.groups || !initialized) return false;
      for (const group of data.groups) {
        for (const perm of group.permissions) {
          if (grants.get(perm.name) !== perm.isGranted) return true;
        }
      }
      return false;
    }, [data, grants, initialized]);

    // Cascade: when toggling a parent, toggle all descendants
    const togglePermission = useCallback(
      (permName: string, checked: boolean) => {
        setGrants((prev) => {
          const next = new Map(prev);
          next.set(permName, checked);

          const cascade = (parent: string) => {
            const children = childrenMap.get(parent) || [];
            for (const child of children) {
              next.set(child, checked);
              cascade(child);
            }
          };
          cascade(permName);

          return next;
        });
      },
      [childrenMap],
    );

    const toggleGroupAll = useCallback(
      (group: PermissionGroup, checked: boolean) => {
        setGrants((prev) => {
          const next = new Map(prev);
          for (const perm of group.permissions) {
            next.set(perm.name, checked);
          }
          return next;
        });
      },
      [],
    );

    const toggleGroupExpanded = useCallback((groupName: string) => {
      setExpandedGroups((prev) => {
        const next = new Set(prev);
        if (next.has(groupName)) {
          next.delete(groupName);
        } else {
          next.add(groupName);
        }
        return next;
      });
    }, []);

    const handleSave = useCallback(() => {
      const permissions: PermissionGrant[] = [];
      for (const [name, isGranted] of grants) {
        permissions.push({ name, isGranted });
      }
      setSaveStatus(null);
      updateMutation.mutate(
        { permissions },
        {
          onSuccess: () => setSaveStatus({ type: 'success', message: 'Permissions saved' }),
          onError: () => setSaveStatus({ type: 'error', message: 'Failed to save permissions' }),
        },
      );
    }, [grants, updateMutation]);

    useImperativeHandle(ref, () => ({
      save: handleSave,
      hasChanges,
    }), [handleSave, hasChanges]);

    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (error) {
      return <Alert severity="error">Failed to load permissions: {error.message}</Alert>;
    }

    if (!data?.groups || data.groups.length === 0) {
      return <Alert severity="info">No permissions available</Alert>;
    }

    const filteredGroups = filterResult
      ? data.groups.filter((g) => filterResult.visibleGroups.has(g.name))
      : data.groups;

    return (
      <Box>
        {saveStatus && (
          <Alert severity={saveStatus.type} onClose={() => setSaveStatus(null)} sx={{ mb: 1 }}>
            {saveStatus.message}
          </Alert>
        )}
        <TextField
          size="small"
          placeholder="Filter permissions..."
          value={filterText}
          onChange={(e) => setFilterText(e.target.value)}
          fullWidth
          sx={{ mb: 1.5 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
              endAdornment: filterText ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setFilterText('')} edge="end">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            },
          }}
        />
        {filteredGroups.length === 0 && debouncedFilter && (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No permissions match "{debouncedFilter}"
          </Typography>
        )}
        {filteredGroups.map((group) => (
          <PermissionGroupSection
            key={group.name}
            group={group}
            grants={grants}
            childrenMap={childrenMap}
            expanded={expandedGroups.has(group.name) || !!filterResult}
            onToggleExpanded={() => toggleGroupExpanded(group.name)}
            onToggle={togglePermission}
            onToggleGroupAll={toggleGroupAll}
            visiblePerms={filterResult?.visiblePerms ?? null}
            matchingPerms={filterResult?.matchingPerms ?? null}
            filterQuery={filterResult ? debouncedFilter.trim().toLowerCase() : null}
          />
        ))}
      </Box>
    );
  },
);

PermissionTree.displayName = 'PermissionTree';

// --- Permission Group Section ---

interface PermissionGroupSectionProps {
  group: PermissionGroup;
  grants: Map<string, boolean>;
  childrenMap: Map<string, string[]>;
  expanded: boolean;
  onToggleExpanded: () => void;
  onToggle: (permName: string, checked: boolean) => void;
  onToggleGroupAll: (group: PermissionGroup, checked: boolean) => void;
  visiblePerms: Set<string> | null;
  matchingPerms: Set<string> | null;
  filterQuery: string | null;
}

function PermissionGroupSection({
  group,
  grants,
  childrenMap,
  expanded,
  onToggleExpanded,
  onToggle,
  onToggleGroupAll,
  visiblePerms,
  matchingPerms,
  filterQuery,
}: PermissionGroupSectionProps) {
  const allGranted = group.permissions.every((p) => grants.get(p.name));
  const noneGranted = group.permissions.every((p) => !grants.get(p.name));
  const grantedCount = group.permissions.filter((p) => grants.get(p.name)).length;

  const rootPerms = group.permissions.filter((p) => !p.parentName);

  return (
    <Box sx={{ mb: 1 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          cursor: 'pointer',
          py: 0.5,
          px: 0.5,
          borderRadius: 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={onToggleExpanded}
      >
        <IconButton size="small" sx={{ p: 0 }}>
          <ExpandMore
            sx={{
              transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
              transition: 'transform 150ms',
              fontSize: '1.25rem',
            }}
          />
        </IconButton>
        <Typography variant="subtitle2" sx={{ flex: 1 }}>
          {group.displayName}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
          {grantedCount}/{group.permissions.length}
        </Typography>
        {expanded && (
          <>
            <Button
              size="small"
              variant="text"
              onClick={(e) => { e.stopPropagation(); onToggleGroupAll(group, true); }}
              disabled={allGranted}
              sx={{ minWidth: 'auto', fontSize: '0.7rem', py: 0, px: 0.5 }}
            >
              All
            </Button>
            <Button
              size="small"
              variant="text"
              onClick={(e) => { e.stopPropagation(); onToggleGroupAll(group, false); }}
              disabled={noneGranted}
              sx={{ minWidth: 'auto', fontSize: '0.7rem', py: 0, px: 0.5 }}
            >
              None
            </Button>
          </>
        )}
      </Box>
      <Collapse in={expanded}>
        <Divider sx={{ mb: 0.5 }} />
        {rootPerms
          .filter((p) => !visiblePerms || visiblePerms.has(p.name))
          .map((perm) => (
            <PermissionNode
              key={perm.name}
              permission={perm}
              grants={grants}
              childrenMap={childrenMap}
              allPermissions={group.permissions}
              onToggle={onToggle}
              depth={0}
              visiblePerms={visiblePerms}
              matchingPerms={matchingPerms}
              filterQuery={filterQuery}
            />
          ))}
      </Collapse>
    </Box>
  );
}

// --- Permission Node (recursive) ---

interface PermissionNodeProps {
  permission: Permission;
  grants: Map<string, boolean>;
  childrenMap: Map<string, string[]>;
  allPermissions: Permission[];
  onToggle: (permName: string, checked: boolean) => void;
  depth: number;
  visiblePerms: Set<string> | null;
  matchingPerms: Set<string> | null;
  filterQuery: string | null;
}

function HighlightText({ text, query }: { text: string; query: string | null }) {
  if (!query) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(query);
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <Box component="span" sx={{ bgcolor: 'warning.light', borderRadius: 0.5, px: 0.25 }}>
        {text.slice(idx, idx + query.length)}
      </Box>
      {text.slice(idx + query.length)}
    </>
  );
}

function PermissionNode({
  permission,
  grants,
  childrenMap,
  allPermissions,
  onToggle,
  depth,
  visiblePerms,
  matchingPerms,
  filterQuery,
}: PermissionNodeProps) {
  const isGranted = grants.get(permission.name) ?? false;
  const children = childrenMap.get(permission.name) || [];
  const childPerms = (
    children
      .map((name) => allPermissions.find((p) => p.name === name))
      .filter(Boolean) as Permission[]
  ).filter((p) => !visiblePerms || visiblePerms.has(p.name));

  const isMatch = matchingPerms?.has(permission.name) ?? false;

  return (
    <Box sx={{ ml: depth * 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          py: 0.25,
          '&:hover': { bgcolor: 'action.hover', borderRadius: 1 },
          ...(isMatch && filterQuery ? { bgcolor: 'action.selected', borderRadius: 1 } : {}),
        }}
      >
        <Checkbox
          checked={isGranted}
          onChange={(_, checked) => onToggle(permission.name, checked)}
          size="small"
          sx={{ p: 0.25 }}
        />
        <Typography variant="body2" sx={{ ml: 0.5 }}>
          <HighlightText text={permission.displayName} query={isMatch ? filterQuery : null} />
        </Typography>
      </Box>

      {childPerms.map((child) => (
        <PermissionNode
          key={child.name}
          permission={child}
          grants={grants}
          childrenMap={childrenMap}
          allPermissions={allPermissions}
          onToggle={onToggle}
          depth={depth + 1}
          visiblePerms={visiblePerms}
          matchingPerms={matchingPerms}
          filterQuery={filterQuery}
        />
      ))}
    </Box>
  );
}
